import * as React from 'react';
import { Fragment } from 'react';
import { compose, setDisplayName, withHandlers, lifecycle, withProps } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { physics, styler } from 'popmotion';
import { ScheduleType } from 'types';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { RecurrenceHourly } from './RecurrenceHourly';
import { RecurrenceDaily } from './RecurrenceDaily';
import { RecurrenceWeekly } from './RecurrenceWeekly';
import { RecurrenceMonthly } from './RecurrenceMonthly';
import { RecurrenceYearly } from './RecurrenceYearly';

type OptionProps = OwnProps & RecomposeProps & TranslateProps & {
    value: ScheduleType;
    textKey: string;
};

const RecurOption = ({ value, reportSettings, handleChange, textKey, t }: OptionProps) =>
    <div>
        <label htmlFor={ `edit-report-schedule-recur-${value.toLowerCase()}` }>
            <input
                type="radio"
                name="edit-report-schedule-recur"
                id={ `edit-report-schedule-recur-${value.toLowerCase()}` }
                value={ value }
                checked={ value === reportSettings.scheduleType }
                onChange={ handleChange }
            />
            { t(textKey) }
        </label>
    </div>
;


export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const RawRecurrence = (props: OwnProps & RecomposeProps & TranslateProps & OemProps) =>
    <Fragment>
        <div style={ props.styles.topMargin }>
            { props.t('Recurrence') }
        </div>
        <div id="edit-schedule-recurrence">
            <div id="edit-schedule-recurrence-options">
                <RecurOption value="Hourly" textKey="2570" { ...props }/>
                <RecurOption value="Daily" textKey="2571" { ...props }/>
                <RecurOption value="Weekly" textKey="2572" { ...props }/>
                <RecurOption value="Monthly" textKey="Monthly" { ...props }/>
                <RecurOption value="Yearly" textKey="Yearly" { ...props }/>
            </div>
            <div id="edit-schedule-recurrence-scroller" style={ props.styles.scroller }>
                <div id="edit-schedule-recurrence-panel-host">
                    <RecurrenceHourly { ...props }/>
                    <RecurrenceDaily { ...props }/>
                    <RecurrenceWeekly { ...props }/>
                    <RecurrenceMonthly { ...props }/>
                    <RecurrenceYearly { ...props }/>
                </div>
            </div>
        </div>
    </Fragment>
;

type RecomposeProps = {
    handleChange: React.ChangeEventHandler<HTMLInputElement>;
};

const scrollTo = (id: string) => {
    const element = document.querySelector(`#${id}`) as HTMLElement;
    if (!element) {
        throw new Error(`Failed to find element with id ${id}`);
    }
    const host = element.parentElement!;
    const hostStyler = styler(host, {});
    
    let currentOffset = 0;
    const match = /translateY\((.*?)px\)/g.exec(host.style.transform || '');
    if (match) {
        currentOffset = parseInt(match[1], 10);
    }

    physics({
        from: currentOffset,
        to: -element.offsetTop,
        springStrength: 200,
        friction: .97,
    }).start((y: number) => hostStyler.set({ y })); // tslint:disable-line
};

export const Recurrence = compose<OwnProps & RecomposeProps & TranslateProps & OemProps, OwnProps>(
    setDisplayName('Recurrence'),
    skin,
    translate(),
    withHandlers({
        handleChange: ({ onChange }: OwnProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const scheduleType = e.target.value as ScheduleType;
            onChange(reportSettings => reportSettings.setScheduleType(scheduleType));
            scrollTo(`edit-schedule-recurrence-panel-${scheduleType.toLowerCase()}`);
        },
    }),
    withProps(({ styles }: OemProps) => ({
        styles: {
            ...styles,
            topMargin: {
                marginTop: '0.5em',
            },
            scroller: {
                borderRight: styles.Bordered.border,
            },
        },
    })),
    lifecycle({
        componentDidMount() {
            const { reportSettings } = this.props as OwnProps;
            const { scheduleType } = reportSettings;
            const host = document.getElementById('edit-schedule-recurrence-panel-host');
            const element = document.getElementById(`edit-schedule-recurrence-panel-${scheduleType.toLowerCase()}`);
            if (host && element) {
                host.style.transform = `translateY(-${element.offsetTop}px)`;
            }
        },
    }),
)(RawRecurrence);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/schedule/runOnSchedule/recurrence/Recurrence.tsx